/**
 * Dédoublonnage des LECTURES en vol.
 *
 * Mesuré sur device : une seule navigation pouvait déclencher 4x tasks,
 * 4x user_profiles, 3x children, 3x /auth/v1/user. Ce ne sont pas des
 * rafraîchissements trop fréquents, ce sont plusieurs composants qui demandent
 * la même donnée en même temps, chacun ouvrant sa propre requête.
 *
 * Deux GET identiques simultanés partagent désormais une seule requête réseau.
 *
 * Garde-fous, volontaires :
 * - Limité aux GET. Les POST/PATCH/DELETE ne sont JAMAIS fusionnés : deux
 *   mutations identiques simultanées sont deux intentions distinctes (deux fois
 *   « ajoute une pièce »), les confondre serait un bug de données. Les RPC
 *   PostgREST étant des POST, elles ne passent pas par ici.
 * - Uniquement PENDANT le vol. Ce n'est pas un cache : rien n'est conservé après
 *   résolution, donc aucun risque de servir une réponse périmée.
 * - La clé inclut l'en-tête Authorization, pour que deux utilisateurs différents
 *   ne partagent jamais une réponse.
 * - Chaque appelant reçoit un clone : un corps de Response ne se lit qu'une
 *   fois, sans clone le deuxième appelant planterait.
 */

function authKeyOf(init?: RequestInit): string {
    const h = init?.headers
    if (!h) return ''
    try {
        if (typeof Headers !== 'undefined' && h instanceof Headers) return h.get('Authorization') ?? ''
        if (Array.isArray(h)) return h.find(([k]) => k.toLowerCase() === 'authorization')?.[1] ?? ''
        const rec = h as Record<string, string>
        return rec.Authorization ?? rec.authorization ?? ''
    } catch {
        return ''
    }
}

function methodOf(input: RequestInfo | URL, init?: RequestInit): string {
    if (init?.method) return init.method.toUpperCase()
    if (typeof Request !== 'undefined' && input instanceof Request) return input.method.toUpperCase()
    return 'GET'
}

function urlOf(input: RequestInfo | URL): string {
    if (typeof input === 'string') return input
    if (typeof Request !== 'undefined' && input instanceof Request) return input.url
    return String(input)
}

/**
 * Enveloppe un `fetch` pour fusionner les GET identiques déjà en vol.
 * `baseFetch` est injectable pour les tests ; par défaut le fetch global, ce
 * qui laisse intact le patch de comptage de perf-metrics (dev).
 */
export function createDedupingFetch(baseFetch?: typeof fetch): typeof fetch {
    const inFlight = new Map<string, Promise<Response>>()

    return (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
        const doFetch = baseFetch ?? fetch
        if (methodOf(input, init) !== 'GET') return doFetch(input, init)

        const key = `${urlOf(input)}\n${authKeyOf(init)}`
        const existing = inFlight.get(key)
        if (existing) return existing.then((r) => r.clone())

        const p = doFetch(input, init)
        inFlight.set(key, p)
        // Retiré dès la résolution : la fenêtre de partage est le vol, rien de plus.
        void p.then(
            () => inFlight.delete(key),
            () => inFlight.delete(key)
        )
        return p.then((r) => r.clone())
    }
}
